/**
 * Filter option hooks for the Mindspace sidebar.
 *
 * Source lessons, source documents and the two tag vocabularies, plus the
 * shared query policy and the mock fallbacks used in `mock` data mode.
 */
import { useQuery } from '@tanstack/react-query';
import { mindspaceKeys } from '@/hooks/mindspace/useMindspace';
import { mindspaceApi } from '@/lib/api-client';
import { loadMockPracticeData } from '@/data';
import { useDataMode } from '@/contexts/DataModeContext';

// Filter-option queries: refetch policy.
//
// These four lists (source lessons, source documents, domain tags,
// user tags) reflect *what exists in the user's mindspace*, which a
// backend Celery sync can mutate at any time without the frontend
// being told. There's no event channel from sync → UI, so we lean on
// React Query's lifecycle:
//
//   - `staleTime: 30s` is a short de-dup window so opening/closing
//     sections in quick succession doesn't fan out duplicate fetches.
//   - `refetchOnMount: 'always'` re-pulls on every nav to the panel,
//     so coming back to Knowledge Archive after a lesson finishes
//     surfaces the new options without a hard refresh.
//   - `refetchOnWindowFocus` is React Query's default (`true`) - tabs
//     back into the app trigger a refetch when stale.
const OPTION_QUERY_POLICY = {
  staleTime: 30 * 1000,
  refetchOnMount: 'always',
} as const;

/** Lessons that contributed concepts to the user's mindspace. */
export function useSourceLessonOptions() {
  const { isMock, isEmpty } = useDataMode();
  return useQuery({
    queryKey: mindspaceKeys.sourceLessons(),
    queryFn: async () => {
      if (isEmpty) return [];
      if (isMock) return await mockSourceLessons();
      try {
        return await mindspaceApi.getSourceLessons();
      } catch (err) {
        console.warn(
          '[useSourceLessonOptions] API failed, falling back to mock',
          err
        );
        return await mockSourceLessons();
      }
    },
    ...OPTION_QUERY_POLICY,
  });
}

async function mockSourceLessons() {
  const seen = new Map<string, string>();
  for (const c of (await loadMockPracticeData()).MOCK_PRACTICE_CONCEPTS) {
    for (const l of c.source_lessons) {
      if (!seen.has(l.lesson_id))
        seen.set(l.lesson_id, l.lesson_title || l.lesson_id);
    }
  }
  return Array.from(seen.entries()).map(([id, title]) => ({ id, title }));
}

/** Uploaded sources whose bytes built any concept. */
export function useSourceDocumentOptions() {
  return useQuery({
    queryKey: mindspaceKeys.sourceDocuments(),
    queryFn: () => mindspaceApi.getSourceDocuments(),
    ...OPTION_QUERY_POLICY,
  });
}

/**
 * Fixed list of every domain tag across the user's mindspace, with
 * per-tag concept counts. The filter UI consumes this so the option
 * list stays stable as other filters narrow the visible graph.
 */
export function useDomainTagOptions() {
  const { isMock, isEmpty } = useDataMode();
  return useQuery({
    queryKey: mindspaceKeys.domainTagOptions(),
    queryFn: async () => {
      if (isEmpty) return [];
      if (isMock) return await mockTagOptions('domain');
      try {
        return await mindspaceApi.getDomainTagOptions();
      } catch (err) {
        console.warn(
          '[useDomainTagOptions] API failed, falling back to mock',
          err
        );
        return await mockTagOptions('domain');
      }
    },
    ...OPTION_QUERY_POLICY,
  });
}

/** Fixed list of every user-curated tag - companion to `useDomainTagOptions`. */
export function useUserTagOptions() {
  const { isMock, isEmpty } = useDataMode();
  return useQuery({
    queryKey: mindspaceKeys.userTagOptions(),
    queryFn: async () => {
      if (isEmpty) return [];
      if (isMock) return await mockTagOptions('user');
      try {
        return await mindspaceApi.getUserTagOptions();
      } catch (err) {
        console.warn(
          '[useUserTagOptions] API failed, falling back to mock',
          err
        );
        return await mockTagOptions('user');
      }
    },
    ...OPTION_QUERY_POLICY,
  });
}

// Synthetic domain-tag bank - only used in mock mode to stress-test
// the tag-picker UI when the user has a large mindspace. Pseudo-random
// counts but deterministic per tag name so renders are stable across
// reloads. Disabled for `user` tags so the two pickers visibly differ.
const SYNTHETIC_DOMAIN_TAGS: string[] = [
  // Math
  'Algebra',
  'Calculus',
  'Linear Algebra',
  'Discrete Math',
  'Probability',
  'Statistics',
  'Number Theory',
  'Combinatorics',
  'Topology',
  'Set Theory',
  'Trigonometry',
  'Geometry',
  'Real Analysis',
  'Complex Analysis',
  'Logic',
  'Differential Equations',
  'Optimization',
  'Game Theory',
  // CS
  'Algorithms',
  'Data Structures',
  'Operating Systems',
  'Databases',
  'Distributed Systems',
  'Compilers',
  'Networking',
  'Cryptography',
  'Computer Graphics',
  'Computer Vision',
  'Machine Learning',
  'Deep Learning',
  'Reinforcement Learning',
  'NLP',
  'Functional Programming',
  'Object-Oriented Programming',
  'Concurrency',
  'Type Systems',
  'Information Theory',
  'Software Engineering',
  'Web Development',
  // Physics
  'Classical Mechanics',
  'Electromagnetism',
  'Thermodynamics',
  'Quantum Mechanics',
  'Relativity',
  'Optics',
  'Astrophysics',
  'Cosmology',
  'Particle Physics',
  'Statistical Mechanics',
  'Fluid Dynamics',
  // Chemistry
  'Organic Chemistry',
  'Inorganic Chemistry',
  'Physical Chemistry',
  'Biochemistry',
  'Analytical Chemistry',
  // Biology
  'Cell Biology',
  'Genetics',
  'Molecular Biology',
  'Evolution',
  'Ecology',
  'Neuroscience',
  'Immunology',
  'Microbiology',
  'Anatomy',
  'Physiology',
  // Humanities
  'Philosophy',
  'Ethics',
  'Logic & Reasoning',
  'Epistemology',
  'Political Philosophy',
  'Linguistics',
  'Rhetoric',
  'Comparative Religion',
  // History
  'Ancient History',
  'Medieval History',
  'Modern History',
  'Renaissance',
  'World War II',
  'Cold War',
  'History of Science',
  // Economics & Business
  'Microeconomics',
  'Macroeconomics',
  'Behavioral Economics',
  'Game Theory',
  'Finance',
  'Accounting',
  'Marketing',
  'Operations',
  // Engineering
  'Mechanical Engineering',
  'Electrical Engineering',
  'Civil Engineering',
  'Chemical Engineering',
  'Control Systems',
  'Signal Processing',
  // Languages
  'Spanish',
  'French',
  'German',
  'Japanese',
  'Mandarin',
  'Latin',
  'Greek',
  // Arts
  'Art History',
  'Music Theory',
  'Composition',
  'Film Studies',
  'Architecture',
  'Photography',
];

async function mockTagOptions(kind: 'domain' | 'user') {
  const counts = new Map<string, number>();
  for (const c of (await loadMockPracticeData()).MOCK_PRACTICE_CONCEPTS) {
    const tags = kind === 'domain' ? c.domain_tags : c.user_tags;
    for (const t of tags) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  // Stress-test seed: layer ~100 synthetic tags onto the domain list so
  // the tag picker has something to scroll through. Counts are derived
  // from a stable hash of the tag name → 1..40 range.
  if (kind === 'domain') {
    for (const tag of SYNTHETIC_DOMAIN_TAGS) {
      if (counts.has(tag)) continue;
      let h = 0;
      for (let i = 0; i < tag.length; i++) {
        h = (h * 31 + tag.charCodeAt(i)) | 0;
      }
      counts.set(tag, 1 + (Math.abs(h) % 40));
    }
  }
  return Array.from(counts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}
